import { useCallback, useState } from 'react'
import * as adminApi from '../../adminApi'
import { ApiError } from '../../api'
import type { AdminInvite, AdminInviteInput, RoomType } from '../../types'
import { ConsoleStats } from './ConsoleStats'
import { InviteCard } from './InviteCard'
import { InviteEditor } from './InviteEditor'

interface ConsoleManagerProps {
  invites: AdminInvite[]
  /** Used to show and change which room each invite holds. */
  roomTypes: RoomType[]
  /** Called after anything is written, so the shell can refresh. */
  onChanged: () => void
  /** Called when a request 401s, so the console can drop back to the prompt. */
  onSessionLost: () => void
}

/** Editing target: an invite id, `new`, or nothing. */
type Editing = string | 'new' | null

/** The guest list: totals, every invite, and the editor. */
export function ConsoleManager({
  invites,
  roomTypes,
  onChanged,
  onSessionLost,
}: ConsoleManagerProps) {
  const [editing, setEditing] = useState<Editing>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Any 401 means the admin session went away, so hand back to the prompt
  // rather than showing an error the user cannot act on.
  const handle = useCallback(
    (err: unknown): string => {
      if (err instanceof ApiError && err.isUnauthenticated) {
        onSessionLost()
        return 'Your session expired.'
      }
      return err instanceof ApiError ? err.message : 'Something went wrong. Please try again.'
    },
    [onSessionLost],
  )

  async function save(input: AdminInviteInput) {
    setSaving(true)
    setSaveError(null)
    try {
      if (editing === 'new') {
        await adminApi.createInvite(input)
      } else if (editing !== null) {
        await adminApi.saveInvite(editing, input)
      }
      setEditing(null)
      onChanged()
    } catch (err) {
      setSaveError(handle(err))
    } finally {
      setSaving(false)
    }
  }

  // InviteCard asks for confirmation itself, inline, so by the time this runs
  // the decision has already been made.
  async function remove(invite: AdminInvite) {
    setBusyId(invite.id)
    try {
      await adminApi.deleteInvite(invite.id)
      onChanged()
    } catch (err) {
      setError(handle(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      {error && (
        <p role="alert" className="text-claret text-sm">
          {error}
        </p>
      )}

      <ConsoleStats invites={invites} roomTypes={roomTypes} />

      <div className="mt-8 flex items-center justify-between">
        <h2 className="text-ink-soft text-xs tracking-[0.25em] uppercase">
          {invites.length} invite{invites.length === 1 ? '' : 's'}
        </h2>
        {editing === null && (
          <button
            type="button"
            onClick={() => {
              setSaveError(null)
              setEditing('new')
            }}
            className="bg-claret hover:bg-claret-dark rounded-md px-4 py-2 text-xs tracking-widest text-white uppercase transition-colors"
          >
            New invite
          </button>
        )}
      </div>

      {editing === 'new' && (
        <div className="mt-4">
          <InviteEditor
            invite={null}
            saving={saving}
            error={saveError}
            onSave={(input) => void save(input)}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      <div className="mt-4 space-y-3">
        {invites.map((invite) =>
          editing === invite.id ? (
            <InviteEditor
              key={invite.id}
              invite={invite}
              saving={saving}
              error={saveError}
              onSave={(input) => void save(input)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <InviteCard
              key={invite.id}
              invite={invite}
              roomTypes={roomTypes}
              busy={busyId === invite.id}
              onEdit={() => {
                setSaveError(null)
                setEditing(invite.id)
              }}
              onDelete={() => void remove(invite)}
              onChanged={onChanged}
              onSessionLost={onSessionLost}
            />
          ),
        )}
      </div>

      {invites.length === 0 && (
        <p className="text-ink-soft mt-4 text-sm">
          No invites yet. Create one above, or import a guest list with the{' '}
          <code className="font-mono">guests</code> command.
        </p>
      )}
    </>
  )
}
